import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import {
  Shield, Users, FileText, CheckCircle, TrendingUp,
  Heart, BarChart3, BookOpen, Lock, DollarSign, MessageSquare
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MinistryDashboardPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, [code]);

  const loadDashboard = async () => {
    try {
      const { data: result } = await axios.get(`${API_URL}/api/ministry/${code}`);
      setData(result);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#36454F] flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-[#E0BFB8] mx-auto mb-4 animate-pulse" strokeWidth={1.5} />
          <p className="text-white/60 font-['Manrope']">Loading impact report...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#36454F] flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <Shield className="w-12 h-12 text-[#E0BFB8] mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="font-['Playfair_Display'] text-2xl text-white mb-2">Dashboard Not Found</h1>
          <p className="text-white/50 font-['Manrope']">This ministry dashboard link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  const { impact, monthly_activity, testimonies, mission } = data;

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="ministry-dashboard">
      {/* Hero Header */}
      <div className="bg-[#36454F] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <Shield className="w-14 h-14 text-[#E0BFB8] mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-semibold mb-3 tracking-tight">
              Selah Slate&trade;
            </h1>
            <p className="text-lg text-white/70 font-['Manrope'] max-w-2xl mx-auto mb-2">
              {mission.tagline}
            </p>
            <p className="text-xs text-white/30 font-['Manrope'] uppercase tracking-widest mt-4">
              Ministry Impact Report
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        {/* Impact Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-testid="impact-cards">
          <ImpactCard icon={Users} value={impact.members_served} label="Members Served" color="#B76E79" />
          <ImpactCard icon={FileText} value={impact.dispute_letters_generated} label="Letters Generated" color="#36454F" />
          <ImpactCard icon={CheckCircle} value={impact.disputes_resolved} label="Disputes Resolved" color="#4CAF50" />
          <ImpactCard icon={DollarSign} value={`$${impact.total_debt_addressed.toLocaleString()}`} label="Debt Addressed" color="#B76E79" isString />
        </div>

        {/* Detailed Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
          <MiniStat value={impact.items_disputed} label="Items Disputed" />
          <MiniStat value={impact.active_tracking} label="Active Tracking" />
          <MiniStat value={impact.credit_reports_analyzed} label="Reports Analyzed" />
          <MiniStat value={impact.testimonies_shared} label="Testimonies Shared" />
        </div>

        {/* Monthly Activity */}
        <Card className="border-[#36454F]/15 bg-white mb-12" data-testid="monthly-activity">
          <CardContent className="p-8">
            <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-6 flex items-center">
              <BarChart3 className="w-6 h-6 mr-3 text-[#B76E79]" strokeWidth={1.5} />
              Monthly Activity
            </h2>
            <div className="grid grid-cols-6 gap-3">
              {monthly_activity.map((month, i) => {
                const maxVal = Math.max(...monthly_activity.map(m => m.users + m.disputes), 1);
                const height = ((month.users + month.disputes) / maxVal) * 100;
                return (
                  <div key={i} className="text-center">
                    <div className="h-32 flex flex-col justify-end items-center mb-2">
                      <div className="w-full max-w-[40px] rounded-t-sm transition-all duration-500" style={{ height: `${Math.max(height, 5)}%`, background: `linear-gradient(to top, #B76E79, #E0BFB8)` }}></div>
                    </div>
                    <p className="text-xs text-[#36454F]/50 font-['Manrope']">{month.month}</p>
                    <p className="text-xs font-['Manrope'] font-semibold text-[#36454F] mt-0.5">
                      {month.users + month.disputes}
                    </p>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-center gap-6 mt-4 text-xs text-[#36454F]/50 font-['Manrope']">
              <span>Combined new users + disputes per month</span>
            </div>
          </CardContent>
        </Card>

        {/* Mission & Values */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <Card className="border-[#E0BFB8] bg-[#E0BFB8]/5" data-testid="mission-card">
            <CardContent className="p-8">
              <BookOpen className="w-8 h-8 text-[#B76E79] mb-4" strokeWidth={1.5} />
              <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-4">Our Mission</h2>
              <p className="font-['Playfair_Display'] text-lg italic text-[#36454F]/80 mb-3 leading-relaxed">
                "{mission.verse}"
              </p>
              <p className="text-sm text-[#36454F]/50 font-['Manrope'] mb-6">— {mission.reference}</p>
              <div className="space-y-3">
                {mission.values.map((value, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <div className="w-5 h-5 rounded-full bg-[#B76E79] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-3 h-3 text-white" strokeWidth={2} />
                    </div>
                    <p className="text-sm text-[#36454F]/80 font-['Manrope']">{value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Testimonies */}
          <Card className="border-[#36454F]/15 bg-white" data-testid="testimonies-section">
            <CardContent className="p-8">
              <MessageSquare className="w-8 h-8 text-[#B76E79] mb-4" strokeWidth={1.5} />
              <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-4">Voices of Impact</h2>
              {testimonies.length === 0 ? (
                <p className="text-[#36454F]/50 font-['Manrope'] italic">Testimonies will appear here as members share their stories.</p>
              ) : (
                <div className="space-y-4">
                  {testimonies.map((t, i) => (
                    <div key={i} className="p-4 bg-[#F5F5F5] rounded-sm border-l-3 border-l-[#B76E79]">
                      <p className="text-sm text-[#36454F]/80 font-['Manrope'] leading-relaxed">"{t.message}"</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-[#36454F]/40 font-['Manrope']">— {t.user_name}</span>
                        {t.scripture_ref && (
                          <span className="text-xs text-[#B76E79] font-['Manrope']">{t.scripture_ref}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* How It Works */}
        <Card className="border-[#36454F]/15 bg-white mb-12" data-testid="how-it-works">
          <CardContent className="p-8">
            <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-8 text-center">
              How Selah Slate Helps Your Members
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: FileText, step: "1", title: "Upload Report", desc: "Members upload their free annual credit report" },
                { icon: TrendingUp, step: "2", title: "AI Analysis", desc: "Our AI identifies errors, collections, and inaccuracies" },
                { icon: Lock, step: "3", title: "Generate Letter", desc: "FCRA Section 611 compliant dispute letters, auto-generated" },
                { icon: Heart, step: "4", title: "Dispute & Breathe", desc: "Mail the letter, track the response, trust the process" }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-14 h-14 rounded-full bg-[#E0BFB8]/30 flex items-center justify-center mx-auto mb-3">
                    <item.icon className="w-6 h-6 text-[#B76E79]" strokeWidth={1.5} />
                  </div>
                  <div className="w-6 h-6 rounded-full bg-[#B76E79] text-white flex items-center justify-center mx-auto mb-2 text-xs font-['Manrope'] font-semibold">
                    {item.step}
                  </div>
                  <h3 className="font-['Manrope'] font-semibold text-[#36454F] mb-1">{item.title}</h3>
                  <p className="text-xs text-[#36454F]/60 font-['Manrope']">{item.desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center mb-16" data-testid="partnership-cta">
          <Card className="border-[#B76E79]/30 bg-[#B76E79]/5 inline-block">
            <CardContent className="p-10">
              <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-3">
                Partner With Us
              </h2>
              <p className="text-[#36454F]/70 font-['Manrope'] max-w-lg mx-auto mb-6">
                Bring Selah Slate to your credit union or church ministry. 
                Free for your members, powerful for your mission.
              </p>
              <a href="mailto:partner@selahslate.com" className="inline-block bg-[#B76E79] hover:bg-[#9e5c66] text-white px-8 py-3 font-['Manrope'] font-medium rounded-sm transition-colors" data-testid="partner-email-btn">
                Get in Touch
              </a>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#36454F] text-white/40 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-6 h-6 text-[#E0BFB8]/50 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-xs font-['Manrope']">© {new Date().getFullYear()} Selah &amp; Stone Legacy Holdings LLC · All rights reserved</p>
          <p className="text-[10px] font-['Manrope'] mt-1 text-white/25">
            No ads. No fees. No credit card offers. Not legal or financial advice.
          </p>
        </div>
      </footer>
    </div>
  );
}

function ImpactCard({ icon: Icon, value, label, color, isString }) {
  return (
    <Card className="border-[#36454F]/15 bg-white shadow-sm">
      <CardContent className="p-6 text-center">
        <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
          <Icon className="w-6 h-6" style={{ color }} strokeWidth={1.5} />
        </div>
        <p className="text-3xl font-['Playfair_Display'] font-semibold text-[#36454F]">
          {isString ? value : value.toLocaleString()}
        </p>
        <p className="text-xs text-[#36454F]/50 font-['Manrope'] mt-1 uppercase tracking-wider">{label}</p>
      </CardContent>
    </Card>
  );
}

function MiniStat({ value, label }) {
  return (
    <div className="p-4 bg-white border border-[#36454F]/10 rounded-sm text-center">
      <span className="text-xl font-['Manrope'] font-semibold text-[#36454F]">{value}</span>
      <p className="text-[10px] text-[#36454F]/40 font-['Manrope'] mt-0.5 uppercase tracking-wider">{label}</p>
    </div>
  );
}
